import React, { useEffect, useState, createContext, useContext } from 'react';
import { useAuth } from './AuthContext';
import { supabaseClient, isSupabaseConfigured, getSupabase } from '../lib/supabase';
export interface ActivityLog {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  details: string;
  type: 'order' | 'company' | 'employee' | 'menu' | 'system' | 'promotion';
}
interface ActivityLogContextType {
  logs: ActivityLog[];
  addLog: (action: string, details: string, type: ActivityLog['type']) => void;
  loading: boolean;
  refreshLogs: () => Promise<void>;
}
const ActivityLogContext = createContext<ActivityLogContextType | undefined>(undefined);
export function ActivityLogProvider({
  children
}: {
  children: ReactNode;
}) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [useSupabase, setUseSupabase] = useState(false);
  const {
    user
  } = useAuth();
  // Load logs from Supabase on mount
  useEffect(() => {
    loadLogs();
  }, []);
  // Real-time subscription to activity_logs table
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('activity-logs-realtime').on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'activity_logs'
    }, () => {
      loadLogsSilent();
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, []);
  // Polling backup every 15s
  useEffect(() => {
    const interval = setInterval(() => {
      loadLogsSilent();
    }, 15000);
    return () => clearInterval(interval);
  }, []);
  const loadLogsSilent = async () => {
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getActivityLogs();
        setLogs(data);
      }
    } catch (error) {
      console.error('Error auto-refreshing activity logs:', error);
    }
  };
  const loadLogs = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        try {
          const data = await supabaseClient.getActivityLogs();
          setLogs(data);
          setUseSupabase(true);
          console.log('✅ Loaded activity logs from Supabase');
        } catch (supabaseError) {
          console.warn('⚠️ Could not load from Supabase, using localStorage:', supabaseError);
          const saved = localStorage.getItem('criket_activity_logs');
          setLogs(saved ? JSON.parse(saved) : []);
          setUseSupabase(false);
        }
      } else {
        // Fallback to localStorage
        const saved = localStorage.getItem('criket_activity_logs');
        setLogs(saved ? JSON.parse(saved) : []);
        setUseSupabase(false);
      }
    } catch (error) {
      console.error('Error loading activity logs:', error);
      // Fallback to localStorage on error
      const saved = localStorage.getItem('criket_activity_logs');
      setLogs(saved ? JSON.parse(saved) : []);
      setUseSupabase(false);
    } finally {
      setLoading(false);
    }
  };
  const addLog = async (action: string, details: string, type: ActivityLog['type']) => {
    const newLog: ActivityLog = {
      id: `log-${Date.now()}`,
      timestamp: new Date().toLocaleString('es-DO'),
      user: user?.name || 'Sistema',
      action,
      details,
      type
    };
    // Optimistically update UI
    setLogs(prev => [newLog, ...prev]);
    // Always save to localStorage as backup
    const updatedLogs = [newLog, ...logs];
    localStorage.setItem('criket_activity_logs', JSON.stringify(updatedLogs));
    // Try to save to Supabase if available
    if (useSupabase && isSupabaseConfigured()) {
      try {
        await supabaseClient.createActivityLog({
          action,
          details,
          type,
          user: user?.name || 'Sistema',
          timestamp: new Date().toISOString()
        });
        console.log('✅ Activity log saved to Supabase');
      } catch (error) {
        console.warn('⚠️ Could not save to Supabase, saved to localStorage instead:', error);
        // Already saved to localStorage above, so we're good
      }
    }
  };
  const refreshLogs = async () => {
    await loadLogs();
  };
  return <ActivityLogContext.Provider value={{
    logs,
    addLog,
    loading,
    refreshLogs
  }} data-id="element-1027">
      {children}
    </ActivityLogContext.Provider>;
}
export function useActivityLog() {
  const context = useContext(ActivityLogContext);
  if (context === undefined) {
    throw new Error('useActivityLog must be used within an ActivityLogProvider');
  }
  return context;
}